<template>
  <div class="time-tracking__list__entry row justify-between items-center gutter-md-x-md gutter-x-xs gutter-y-sm q-pa-md">
    <div class="col">
      <inline-edit-input
        :readonly="!userIsOwnerOfEntryOrAdmin"
        :placeholder="$t('no_description')"
        :value="timeEntry.description"
        @save="handleSaveDescription"
        @click="checkUserHasSufficientPermissions"
      />

      <div class="row items-center" v-if="showUser">
        <q-icon name="person" color="faded" size="12px" class="gt-xs"/>
        <span class="q-caption text-faded q-ml-sm-sm q-ml-none">{{ userName }}</span>
      </div>
    </div>

    <div v-if="timeEntry.billed" class="col-auto">
      <q-chip
        dense
        color="positive"
        text-color="white"
        icon="check"
        class="gt-md"
      >
        {{ $t('billed') }}
      </q-chip>

      <q-icon name="check" color="positive" class="lt-lg">
        <q-tooltip :delay="300">
          {{ $t('billed_tooltip') }}
        </q-tooltip>
      </q-icon>
    </div>

    <div class="col-5 col-lg-4 col-xl-3 row gt-sm">
      <div class="col">
        <customer-indicator clickable :customer="timeEntry.customer ? timeEntry.customer.data : undefined"/>

        <customer-popover
          :active-customer="timeEntry.customer ? timeEntry.customer.data : undefined"
          @select-customer="handleSelectCustomer"
        />
      </div>

      <div class="col">
        <project-indicator
          :project="timeEntry.project ? timeEntry.project.data : undefined"
          clickable
        />

        <project-popover
          :active-project="timeEntry.project ? timeEntry.project.data : undefined"
          @select-project="handleSelectProject"
        />
      </div>
    </div>

    <div class="col-auto">
      <q-checkbox
        keep-color
        checked-icon="attach_money"
        unchecked-icon="money_off"
        :color="timeEntry.billable ? 'faded' : 'light'"
        :disabled="!userIsOwnerOfEntryOrAdmin"
        :value="timeEntry.billable"
        @input="handleToggleBillable"
      />
    </div>

    <div class="col-auto">
      <inline-edit-duration
        v-if="!isRunning"
        :duration="timeEntry.duration"
        :started-at="timeEntry.started_at.date"
        :stopped-at="timeEntry.stopped_at.date"
        :disabled="!userIsOwnerOfEntryOrAdmin"
        @save="handleSaveDuration"
        @click="checkUserHasSufficientPermissions"
      />

      <span v-else class="text-faded text-italic">{{ $t('running') }}</span>
    </div>

    <div class="col-auto">
      <q-btn
        flat
        dense
        round
        color="light"
        icon="play_arrow"
        @click="handleResumeTimeEntry"
      />

      <q-btn
        flat
        dense
        round
        color="light"
        icon="delete"
        :class="{ 'disabled': !userIsOwnerOfEntryOrAdmin, 'non-selectable': !userIsOwnerOfEntryOrAdmin }"
        @click="handleDeleteTimeEntry"
      />
    </div>

    <!-- Only displayed on narrow devices -->
    <div class="col-12 row lt-md">
      <div class="col">
        <customer-indicator clickable :customer="timeEntry.customer ? timeEntry.customer.data : undefined"/>

        <customer-popover
          :active-customer="timeEntry.customer ? timeEntry.customer.data : undefined"
          @select-customer="handleSelectCustomer"
        />
      </div>

      <div class="col">
        <project-indicator
          :project="timeEntry.project ? timeEntry.project.data : undefined"
          clickable
        />

        <project-popover
          :active-project="timeEntry.project ? timeEntry.project.data : undefined"
          @select-project="handleSelectProject"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import CustomerIndicator from 'components/TimeTracking/CustomerIndicator.vue'
  import CustomerPopover from 'components/TimeTracking//CustomerPopover.vue'
  import InlineEditDuration from 'components/Common/Inputs/InlineEditDuration.vue'
  import InlineEditInput from 'components/Common/Inputs/InlineEditInput.vue'
  import ProjectIndicator from 'components/TimeTracking/ProjectIndicator.vue'
  import ProjectPopover from 'components/TimeTracking//ProjectPopover.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { currentUtcDateTime } from 'src/utils/date'
  import { date } from 'quasar'

  export default {
    props: {
      timeEntry: {
        type: Object,
        required: true
      },

      showUser: {
        type: Boolean,
        default: true
      }
    },

    computed: {
      ...mapGetters(['activeTeam', 'user', 'activeTimeEntry']),

      isRunning () {
        return !this.timeEntry.stopped_at
      },

      userName () {
        return this.timeEntry.user ? [this.timeEntry.user.data.first_name, this.timeEntry.user.data.last_name].join(' ').trim() : null
      },

      userIsOwnerOfEntryOrAdmin () {
        return this.timeEntry.user_id === this.user.id || (this.timeEntry.team_id === this.activeTeam.id && this.user.isAdminOf(this.activeTeam))
      }
    },

    methods: {
      ...mapActions(['createTimeEntry', 'updateTimeEntry', 'deleteTimeEntry', 'fetchSelectedProject']),

      handleResumeTimeEntry () {
        this.stopActiveTimeEntry()
          .then(() => {
            const timeEntry = {
              description: this.timeEntry.description,
              billable: this.timeEntry.billable,
              project_id: this.timeEntry.project_id,
              customer_id: this.timeEntry.customer_id,
              started_at: date.formatDate(currentUtcDateTime(), 'YYYY-MM-DD HH:mm:ss')
            }

            this.createTimeEntry(timeEntry)
          })
      },

      handleDeleteTimeEntry () {
        if (!this.checkUserHasSufficientPermissions()) {
          return
        }

        this.deleteTimeEntry(this.timeEntry)
          .then(this.fetchSelectedProject(this.timeEntry.project_id))
      },

      handleSaveDescription (value) {
        this.updateTimeEntryData({
          description: value
        })
      },

      handleSaveDuration (value) {
        this.updateTimeEntryData(value)
      },

      handleToggleBillable () {
        if (!this.checkUserHasSufficientPermissions()) {
          return
        }

        this.updateTimeEntryData({
          billable: !this.timeEntry.billable
        })
      },

      handleSelectProject (project) {
        const projectId = project ? project.id : null

        let data = {
          'project_id': projectId
        }

        if (project && project.customer_id) {
          data['customer_id'] = project.customer_id
        }

        this.updateTimeEntryData(data)
      },

      handleSelectCustomer (customer) {
        const customerId = customer ? customer.id : null

        let data = {
          'customer_id': customerId
        }

        this.updateTimeEntryData(data)
      },

      updateTimeEntryData (data) {
        return this.updateTimeEntry({
          timeEntryId: this.timeEntry.id,
          timeEntry: data
        })
          .catch(createErrorFromResponse)
      },

      checkUserHasSufficientPermissions () {
        if (this.userIsOwnerOfEntryOrAdmin) {
          return true
        }

        this.$q.dialog({
          title: this.$t('insufficient_permissions'),
          message: this.$t('time_entry_insufficient_permissions_warning'),
          ok: this.$t('ok')
        })

        return false
      },

      stopActiveTimeEntry () {
        if (this.activeTimeEntry) {
          return this.updateTimeEntry({
            timeEntryId: this.activeTimeEntry.id,
            timeEntry: { stopped_at: date.formatDate(currentUtcDateTime(), 'YYYY-MM-DD HH:mm:ss') }
          })
        } else {
          return Promise.resolve()
        }
      }
    },

    components: {
      CustomerIndicator,
      CustomerPopover,
      ProjectIndicator,
      InlineEditDuration,
      InlineEditInput,
      ProjectPopover
    }
  }
</script>
